import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../api';

const LocationContext = createContext(null);
const STORAGE_KEY = 'aria_location_context';

export const GLOBAL_ROLES = ['admin', 'executive', 'sales_manager', 'finance_user', 'viewer'];

export function LocationProvider({ children }) {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [activeLocation, setActiveLocation] = useState(null); // null = "All Locations"
  const [orgProfile, setOrgProfile] = useState(null);
  const [loadingLocs, setLoadingLocs] = useState(true);

  const isGlobal = GLOBAL_ROLES.includes(user?.role);

  // Fetch org profile (for sidebar branding)
  useEffect(() => {
    if (!user) return;
    api.get('/organization').then(r => setOrgProfile(r.data)).catch(() => {});
  }, [user]);

  // Fetch locations and restore active context
  useEffect(() => {
    if (!user) { setLoadingLocs(false); return; }
    api.get('/locations?status=active').then(r => {
      const locs = r.data || [];
      setLocations(locs);

      const savedCode = localStorage.getItem(STORAGE_KEY);
      const restored = locs.find(l => l.code === savedCode)
        || locs.find(l => l.code === user.primary_location);

      if (restored) {
        setActiveLocation(restored);
      } else if (isGlobal) {
        setActiveLocation(null); // All Locations
      } else {
        setActiveLocation(locs[0] || null);
      }
    }).catch(() => {}).finally(() => setLoadingLocs(false));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchLocation = useCallback((code) => {
    if (!code) {
      setActiveLocation(null);
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    const loc = locations.find(l => l.code === code);
    setActiveLocation(loc || null);
    if (loc) localStorage.setItem(STORAGE_KEY, code);
    else localStorage.removeItem(STORAGE_KEY);
  }, [locations]);

  const refreshLocations = useCallback(() => {
    return api.get('/locations?status=active').then(r => setLocations(r.data || []));
  }, []);

  const refreshOrg = useCallback(() => {
    return api.get('/organization').then(r => setOrgProfile(r.data)).catch(() => {});
  }, []);

  return (
    <LocationContext.Provider value={{
      locations,
      activeLocation,
      switchLocation,
      isGlobal,
      orgProfile,
      loadingLocs,
      refreshLocations,
      refreshOrg,
    }}>
      {children}
    </LocationContext.Provider>
  );
}

export const useLocationContext = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocationContext must be used inside LocationProvider');
  return ctx;
};
